import { HTTP_STATUS } from "@lhe/shared";
import type {
  AiEditJobCreated,
  AiEditJobRequest,
  AiEditJobStatus,
  AiEditResponse,
  ApiOperationDetail,
  ApiOperationSummary,
  ApiSpecSummary,
  AiGenerateResponse,
  AiModel,
  ApiResult,
  AuthConfig,
  DeployRequest,
  DeployReview,
  DeployResult,
  DeployStatus,
  DeployTarget,
  FigmaConnectionStatus,
  FigmaConnectionRequest,
  FigmaImportJobCreated,
  FigmaImportJobStatus,
  FigmaImportRequest,
  FigmaPluginImportState,
  FigmaPluginImportSummary,
  FigmaPluginPairingSession,
  DesignSystemManifest,
  DesignSystemSummary,
  DesktopRelease,
  RegisterApiSpecInput,
  DocumentKind,
  DocumentSummary,
  EditorDocument,
  Folder,
  User,
  UserSettings,
  WorkspaceTree,
} from "@lhe/shared";

/** 서버가 돌려준 ApiError 를 그대로 담는 예외. code 로 분기한다. */
export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    /**
     * 서버가 함께 보낸 진단 정보.
     *
     * Omni 가 돌려준 응답 조각, zod 검증 실패 내역 같은 것들이다. 사용자에게
     * 바로 보여줄 문장은 아니지만 **원인을 찾을 때는 이게 거의 전부다** —
     * "Omni 서버가 502 를 반환했습니다" 만으로는 무엇이 잘못됐는지 알 수 없다.
     * 그래서 버리지 않고 실패 상세 화면까지 들고 간다.
     */
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

/* ── 세션 만료 알림 ──────────────────────────────────────── */

type UnauthenticatedListener = () => void;
const unauthenticatedListeners = new Set<UnauthenticatedListener>();

/**
 * 서버가 `UNAUTHENTICATED` 를 돌려줄 때마다 불린다.
 *
 * **여기 한 곳에서만 알린다.** 세션은 작업 중 언제든(웹 세션 만료, 장기간 열지 않은
 * 데스크톱 앱, 다른 탭 로그아웃) 끊길 수 있고, 그 뒤로는 모든 요청이 401 이 된다. 화면마다
 * 401 을 따로 처리하면 어딘가는 빠뜨려서, 사용자는 저장이 조용히 안 되는
 * 상태로 계속 작업하게 된다.
 *
 * 구독 해제 함수를 돌려준다.
 */
export function onUnauthenticated(
  listener: UnauthenticatedListener,
): () => void {
  unauthenticatedListeners.add(listener);
  return () => {
    unauthenticatedListeners.delete(listener);
  };
}

/**
 * 401 을 알린다.
 *
 * `request()` 를 거치지 않는 요청(multipart 업로드)도 있어 밖으로 열어 둔다.
 * 실제로 만료를 화면에 띄울지는 구독하는 쪽이 판단한다 — 아직 로그인한 적 없는
 * 사용자의 `/auth/me` 401 은 만료가 아니라 그냥 미로그인이다.
 */
export function notifyUnauthenticated(): void {
  for (const listener of unauthenticatedListeners) listener();
}

const ERROR_BODY_PREVIEW_CHARS = 2_000;

interface ApiRequestContext {
  path: string;
  method?: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isApiResult<T>(value: unknown): value is ApiResult<T> {
  if (!isRecord(value) || typeof value.ok !== "boolean") return false;
  if (value.ok) return "data" in value;
  return (
    isRecord(value.error) &&
    typeof value.error.code === "string" &&
    typeof value.error.message === "string"
  );
}

function responseHeadersForDiagnostics(response: Response) {
  const names = [
    "content-type",
    "content-length",
    "cf-ray",
    "server",
    "retry-after",
    "x-request-id",
    "x-omniroute-request-id",
  ];
  return Object.fromEntries(
    names.flatMap((name) => {
      const value = response.headers.get(name);
      return value ? [[name, value]] : [];
    }),
  );
}

/**
 * JSON이 아닌 프록시·플랫폼 오류도 원인을 잃지 않고 ApiRequestError로 바꾼다.
 * 요청 본문과 쿠키는 기록하지 않고 경로, 상태, 안전한 응답 헤더와 본문 앞부분만 남긴다.
 */
export async function readApiResponse<T>(
  response: Response,
  context: ApiRequestContext,
): Promise<T> {
  let rawBody = "";
  let readError: string | undefined;
  try {
    rawBody = await response.text();
  } catch (cause) {
    readError = cause instanceof Error ? cause.message : String(cause);
  }

  let parsed: unknown;
  let parseError: string | undefined;
  if (rawBody) {
    try {
      parsed = JSON.parse(rawBody) as unknown;
    } catch (cause) {
      parseError = cause instanceof Error ? cause.message : String(cause);
    }
  }

  const request = {
    method: context.method ?? "GET",
    path: context.path,
  };
  const responseSummary = {
    url: response.url || undefined,
    status: response.status,
    statusText: response.statusText || undefined,
    headers: responseHeadersForDiagnostics(response),
  };

  if (!isApiResult<T>(parsed)) {
    const details = {
      kind: "invalid_api_response",
      request,
      response: responseSummary,
      ...(readError ? { readError } : {}),
      ...(parseError ? { parseError } : {}),
      bodyLength: rawBody.length,
      bodyStart: rawBody.slice(0, ERROR_BODY_PREVIEW_CHARS),
    };
    console.error("[api] 서버 응답을 해석하지 못했습니다.", details);
    throw new ApiRequestError(
      "INTERNAL",
      response.status === 503
        ? "서버가 일시적으로 요청을 처리하지 못했습니다. 콘솔의 API 오류 정보를 확인해 주세요."
        : "서버 응답을 해석하지 못했습니다. 콘솔의 API 오류 정보를 확인해 주세요.",
      response.status,
      details,
    );
  }

  if (!parsed.ok) {
    const diagnostic = {
      request,
      response: responseSummary,
      error: parsed.error,
    };
    if (parsed.error.code !== "UNAUTHENTICATED") {
      console.error("[api] 요청이 실패했습니다.", diagnostic);
    }
    if (parsed.error.code === "UNAUTHENTICATED") notifyUnauthenticated();
    throw new ApiRequestError(
      parsed.error.code,
      parsed.error.message,
      response.status,
      parsed.error.details,
    );
  }
  return parsed.data;
}

/**
 * 모든 API 호출은 이 함수를 거친다.
 * `credentials: 'include'` 가 세션 쿠키를 실어 보내므로 토큰을 직접 다루지 않는다.
 */
async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = init?.method ?? "GET";
  const response = await fetch(`/api${path}`, {
    ...init,
    credentials: "include",
    headers: {
      ...(init?.body ? { "content-type": "application/json" } : {}),
      ...init?.headers,
    },
  });
  return readApiResponse<T>(response, { path: `/api${path}`, method });
}

const json = (method: string, body: unknown): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

const AI_EDIT_POLL_INTERVAL_MS = 900;

const waitForNextAiEditPoll = () =>
  new Promise<void>((resolve) => setTimeout(resolve, AI_EDIT_POLL_INTERVAL_MS));

/** 짧은 상태 조회만 반복해 긴 Omni 호출이 브라우저 HTTP 요청에 매이지 않게 한다. */
export async function waitForAiEdit(
  id: string,
  wait: () => Promise<void> = waitForNextAiEditPoll,
  signal?: AbortSignal,
): Promise<AiEditResponse> {
  let pollingFailures = 0;
  while (true) {
    signal?.throwIfAborted();
    await wait();
    signal?.throwIfAborted();

    let status: AiEditJobStatus;
    try {
      status = await request<AiEditJobStatus>(
        `/ai/edit/${encodeURIComponent(id)}`,
        signal ? { signal } : undefined,
      );
      pollingFailures = 0;
    } catch (cause) {
      signal?.throwIfAborted();
      if (
        cause instanceof ApiRequestError &&
        cause.code === "UNAUTHENTICATED"
      ) {
        throw cause;
      }
      pollingFailures += 1;
      if (pollingFailures >= 5) throw cause;
      continue;
    }

    if (status.state === "failed") {
      const error = status.error ?? {
        code: "INTERNAL" as const,
        message: "AI 편집을 완료하지 못했습니다.",
      };
      throw new ApiRequestError(
        error.code,
        error.message,
        HTTP_STATUS[error.code],
        error.details,
      );
    }
    if (status.state !== "completed") continue;
    if (!status.result) {
      throw new ApiRequestError(
        "INTERNAL",
        "완료된 AI 편집 결과가 없습니다.",
        HTTP_STATUS.INTERNAL,
      );
    }
    return status.result;
  }
}

export type { AuthConfig };

export const api = {
  auth: {
    /** 로그인 화면이 어떤 버튼을 그릴지 결정한다. 인증 없이 호출 가능. */
    config: () => request<AuthConfig>("/auth/config"),
    me: () => request<User>("/auth/me"),
    /** Electron 시작 시 영속 쿠키와 서버 세션 만료를 함께 연장한다. */
    renewDesktopSession: () =>
      request<{ user: User; expiresAt: number }>("/auth/desktop-session", {
        method: "POST",
      }),
    logout: () =>
      request<{ loggedOut: boolean }>("/auth/logout", { method: "POST" }),
    /** Google 로그인. Firebase 에서 받은 ID 토큰을 넘겨 서버 세션을 만든다. */
    google: (idToken: string, persistent = false) =>
      request<User>("/auth/google", json("POST", { idToken, persistent })),
    /** ⚠️ 개발 전용. 운영에서는 서버가 403 을 돌려준다. */
    testLogin: (userId: string, persistent = false) =>
      request<{ id: string }>(
        "/auth/test-login",
        json("POST", { userId, persistent }),
      ),
  },

  /** 랜딩이 부르는 공개 경로. 로그인 없이 호출된다. */
  landing: {
    desktopRelease: () => request<DesktopRelease>("/desktop/latest"),
  },

  workspace: {
    tree: () => request<WorkspaceTree>("/workspace"),
    settings: () => request<UserSettings>("/workspace/settings"),
    saveSettings: (patch: UserSettings) =>
      request<UserSettings>("/workspace/settings", json("PUT", patch)),
    createFolder: (input: {
      name: string;
      slug: string;
      parentId: string | null;
    }) => request<Folder>("/workspace/folders", json("POST", input)),
    /** 이름 변경과 이동(parentId)을 함께 처리한다. 모두 선택 항목이다. */
    updateFolder: (
      id: string,
      patch: { name?: string; slug?: string; parentId?: string | null },
    ) => request<Folder>(`/workspace/folders/${id}`, json("PUT", patch)),
    deleteFolder: (id: string) =>
      request<{ deleted: boolean }>(`/workspace/folders/${id}`, {
        method: "DELETE",
      }),
  },

  documents: {
    list: () => request<DocumentSummary[]>("/documents"),
    get: (id: string) => request<EditorDocument>(`/documents/${id}`),
    /** 좌측 트리로부터 계산된 배포 경로. */
    path: (id: string) => request<{ path: string }>(`/documents/${id}/path`),
    create: (input: {
      title: string;
      slug: string;
      folderId: string | null;
      kind: DocumentKind;
      designSystemId: string;
    }) => request<EditorDocument>("/documents", json("POST", input)),
    update: (
      id: string,
      patch: Partial<
        Pick<
          EditorDocument,
          | "title"
          | "slug"
          | "folderId"
          | "html"
          | "designSystemId"
          | "embed"
          | "seo"
        >
      >,
    ) => request<EditorDocument>(`/documents/${id}`, json("PUT", patch)),
    remove: (id: string) =>
      request<{ deleted: boolean }>(`/documents/${id}`, { method: "DELETE" }),
  },

  designSystems: {
    list: () => request<DesignSystemSummary[]>("/design-systems"),
    get: (id: string) => request<DesignSystemManifest>(`/design-systems/${id}`),
  },

  ai: {
    /** 이미지 입력이 가능한 모델 조회. 기본적으로 supportsImages=true 로 필터링된다. */
    models: (params?: { supportsImages?: boolean }) => {
      const supportsImages = params?.supportsImages ?? true;
      const query = supportsImages
        ? "?supportsImages=true"
        : "?supportsImages=false";
      return request<AiModel[]>(`/ai/models${query}`);
    },
    edit: async (
      input: AiEditJobRequest,
      options?: { signal?: AbortSignal },
    ) => {
      const created = await request<AiEditJobCreated>("/ai/edit", {
        ...json("POST", input),
        ...options,
      });
      return waitForAiEdit(created.id, waitForNextAiEditPoll, options?.signal);
    },
    generate: (input: {
      brief: string;
      kind: DocumentKind;
      designSystemId: string;
      modelId?: string;
      imageIds?: string[];
    }) => request<AiGenerateResponse>("/ai/generate", json("POST", input)),
  },

  uploads: {
    /** 업로드는 multipart 라 lib/images.ts 의 uploadImage() 를 쓴다. */
    remove: (id: string) =>
      request<{ deleted: boolean }>(`/uploads/${id}`, { method: "DELETE" }),
    url: (id: string) => `/api/uploads/${id}`,
  },

  /** 등록된 Swagger/OpenAPI 문서와 그 안의 API 목록. */
  apiSpecs: {
    list: () => request<ApiSpecSummary[]>("/api-specs"),
    /** url 또는 content 중 하나만 넣는다. 서버가 파싱해 우리 형태로 줄여 저장한다. */
    register: (input: RegisterApiSpecInput) =>
      request<ApiSpecSummary>("/api-specs", json("POST", input)),
    rename: (id: string, name: string) =>
      request<ApiSpecSummary>(`/api-specs/${id}`, json("PATCH", { name })),
    /** url 로 등록한 것만 가능하다. 붙여넣기로 등록한 스펙은 400 이 온다. */
    refresh: (id: string) =>
      request<ApiSpecSummary>(`/api-specs/${id}/refresh`, { method: "POST" }),
    remove: (id: string) =>
      request<{ deleted: boolean }>(`/api-specs/${id}`, { method: "DELETE" }),

    /** 목록용. 파라미터·스키마는 빠져 있다 — 고른 뒤 operation() 으로 받는다. */
    operations: (
      id: string,
      params: { q?: string; tag?: string; limit?: number },
    ) => {
      const query = new URLSearchParams();
      if (params.q) query.set("q", params.q);
      if (params.tag) query.set("tag", params.tag);
      if (params.limit) query.set("limit", String(params.limit));
      const suffix = query.toString();
      return request<{ items: ApiOperationSummary[]; total: number }>(
        `/api-specs/${id}/operations${suffix ? `?${suffix}` : ""}`,
      );
    },

    /**
     * 오퍼레이션 하나의 상세.
     * 식별자를 쿼리로 넘긴다 — operationId 가 없는 스펙에서는 `get:/v1/x` 처럼
     * 슬래시가 들어가 경로 세그먼트로 쓸 수 없다.
     */
    operation: (id: string, operationId: string) =>
      request<ApiOperationDetail>(
        `/api-specs/${id}/operation?operationId=${encodeURIComponent(operationId)}`,
      ),
  },

  /** Figma 링크 연동. PAT는 연결 요청에만 보내며 어느 응답에도 담기지 않는다. */
  figma: {
    status: () => request<FigmaConnectionStatus>("/figma/status"),
    connect: (input: FigmaConnectionRequest) =>
      request<FigmaConnectionStatus>("/figma/connection", json("POST", input)),
    disconnect: () =>
      request<{ disconnected: boolean }>("/figma/connection", {
        method: "DELETE",
      }),
    /** 작업을 만들고 즉시 id를 받는다. 실제 처리는 서버 Workflow가 이어서 수행한다. */
    startImport: (input: FigmaImportRequest) =>
      request<FigmaImportJobCreated>("/figma/import", json("POST", input)),
    importStatus: (id: string) =>
      request<FigmaImportJobStatus>(`/figma/import/${encodeURIComponent(id)}`),
    /** sandbox iframe이 비공개 R2 후보 HTML을 직접 읽는 주소. */
    importPreviewUrl: (id: string, revision: number) =>
      `/api/figma/import/${encodeURIComponent(id)}/preview?revision=${revision}`,
    cancelImport: (id: string) =>
      request<FigmaImportJobStatus>(`/figma/import/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
  },

  /** Figma 플러그인 페어링·비공개 수신함. 플러그인용 Bearer 티켓은 웹이 받지 않는다. */
  figmaPlugin: {
    markDesktopPresent: () =>
      request<{ active: true }>("/figma-plugin/desktop-presence", {
        method: "PUT",
      }),
    createPairing: () =>
      request<FigmaPluginPairingSession>("/figma-plugin/pairing-sessions", {
        method: "POST",
      }),
    cancelPairing: (id: string) =>
      request<{ deleted: boolean }>(
        `/figma-plugin/pairing-sessions/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
        },
      ),
    imports: (filter?: {
      state?: FigmaPluginImportState;
      documentId?: string;
    }) => {
      const query = new URLSearchParams();
      if (filter?.state) query.set("state", filter.state);
      if (filter?.documentId) query.set("documentId", filter.documentId);
      const suffix = query.toString();
      return request<FigmaPluginImportSummary[]>(
        `/figma-plugin/imports${suffix ? `?${suffix}` : ""}`,
      );
    },
    attach: (id: string, documentId: string) =>
      request<FigmaPluginImportSummary>(
        `/figma-plugin/imports/${encodeURIComponent(id)}`,
        json("PATCH", { action: "attach", documentId }),
      ),
    detach: (id: string) =>
      request<FigmaPluginImportSummary>(
        `/figma-plugin/imports/${encodeURIComponent(id)}`,
        json("PATCH", { action: "detach" }),
      ),
    markUsed: (id: string) =>
      request<FigmaPluginImportSummary>(
        `/figma-plugin/imports/${encodeURIComponent(id)}`,
        json("PATCH", { action: "use" }),
      ),
    remove: (id: string) =>
      request<{ deleted: boolean }>(
        `/figma-plugin/imports/${encodeURIComponent(id)}`,
        {
          method: "DELETE",
        },
      ),
  },

  deploy: {
    targets: () => request<DeployTarget[]>("/deploy/targets"),
    /** 배포 전 확인용. 이미 올라간 것이 있으면 재배포(덮어쓰기)라는 뜻이다. */
    status: (documentId: string) =>
      request<DeployStatus>(
        `/deploy/status?documentId=${encodeURIComponent(documentId)}`,
      ),
    /** 저장된 현재 HTML을 LLM으로 검토하고 이 저장본에 묶인 배포 토큰을 받는다. */
    review: (input: { documentId: string; targetId: string }) =>
      request<DeployReview>("/deploy/review", json("POST", input)),
    /** 경로는 서버가 좌측 트리에서 계산한다. 클라이언트가 지정하지 않는다. */
    run: (input: DeployRequest) =>
      request<DeployResult>("/deploy", json("POST", input)),
  },
};
